import React, { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { type SxProps } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import delay from 'lodash/delay';
import MenuModal from '../components/modals/menu-modal';
import MenuFeed from '../components/menus/menu-feed';
import MenuFeedItem from '../components/menus/menu-feed-item';
import * as selectors from './batch-menu.selectors';
import * as actions from './batch-menu.slice';
import { useAppSelector, useAppDispatch } from '../hooks';
import { useGetBatchesQuery } from '../services/batch.service';
import {
  BatchFetchResponse,
  calculateProgress,
  renderCaption,
  renderPrimaryText,
} from '../models/batch';
import Poster from '../components/poster';
import TimeImage from '../images/time';
import { useGetFormNamesQuery } from '../services/forms.service';

export const Center = ({ ...props }: { children: JSX.Element, height: number, sx?: SxProps }) => (
  <Box height={props.height} overflow="hidden" sx={props.sx}>
    <Stack
      justifyContent="center"
      alignItems="center"
      height={props.height}
    >
      {props.children}
    </Stack>
  </Box>
);

export default function ActivityCenter() {
  const dispatch = useAppDispatch();
  const openBatchProcessMenu = useAppSelector(selectors.openBatchProcessSelector);
  const [skip, setSkip] = useState(true);

  const [pollingInterval, setPollingInterval] = useState(2000);
  const { data: batchesResponse, isLoading } = useGetBatchesQuery({ activity: true }, {
    pollingInterval,
    skip,
  });

  const batches = ((batchesResponse as BatchFetchResponse)?.batches ?? []);
  const { data: formNamesHash } = useGetFormNamesQuery(batches.map((batch) => batch.form_ids![0]), {
    skip: !batches,
  });

  useEffect(() => {
    if (!openBatchProcessMenu) {
      delay(() => setSkip(true), 1000);
    } else {
      setSkip(false);
    }
  }, [openBatchProcessMenu]);

  useEffect(() => {
    const activeBatches = batches.filter(
      (batch) => (['pending', 'running'].includes(batch.status)),
    );

    if (activeBatches.length > 0) {
      setPollingInterval(2000);
    } else {
      // we dispatch the event so that it registers as a change in the data-viewer HOC
      const event = new Event('onprocesscomplete');
      window.dispatchEvent(event);
      setPollingInterval(0);
    }
  }, [setPollingInterval, batchesResponse]);

  return (
    <MenuModal
      noPadding
      noActions
      titleSmall
      anchorEl={openBatchProcessMenu}
      title="ACTIVITY"
      width={600}
      onClose={() => dispatch(actions.setOpenBatchProcess(null))}
    >
      {isLoading && (
        <Center height={450}>
          <CircularProgress />
        </Center>
      )}
      {!isLoading && !batches.length && (
        <Center height={450}>
          <Poster Image={TimeImage} heading="No Activity" />
        </Center>
      )}
      {!isLoading && !!batches.length && (
        <MenuFeed maxHeight={480}>
          {batches.map((batch, idx) => {
            const batchAction = batch.actions[0];
            return (
              <MenuFeedItem
                {...batch}
                key={batch.id}
                primaryText={renderPrimaryText(
                  batch.status, batch.objects_success, batch.objects_total, batchAction.type,
                )}
                caption={renderCaption(batch.status, (batch.started_at ?? batch.created_at), batchAction.type)}
                statusValue={calculateProgress(batch.objects_success, batch.objects_total)}
                activity={batchAction}
                secondaryText={formNamesHash?.[batch.form_ids![0] as string]}
                isLastItem={idx === (batches.length - 1)}
              />
            );
          })}
        </MenuFeed>
      )}
    </MenuModal>
  );
}
